// « Install the app » — the admin as a home-screen app (2026-10-01).
//
// Android / desktop Chrome: Chrome says the site can be installed
// (`beforeinstallprompt`, kept by lib/pwa/installPrompt.ts) → an Install button
// that opens Chrome's own dialog.
// iPhone: there is no install prompt at all. The only way is Safari's Share
// sheet, so the card shows those steps. Opening Storiz FROM THE NEW ICON is
// part of the steps on purpose — on iPhone, notifications only exist there.
// Hidden once running as the installed app, and dismissible.
import { useState, useSyncExternalStore } from 'react';
import { Button } from '@/components/ui/Button';
import { installPrompt as appInstallPrompt, type InstallPromptStore } from '@/lib/pwa/installPrompt';
import { installCardState } from '@/lib/pwa/cardState';
import { isIosDevice, isStandalone } from '@/lib/pwa/detect';
import { PhoneCard, PhoneIcon, ShareIcon, AddSquareIcon } from './phoneAppUi';

export const INSTALL_DISMISSED_KEY = 'storiz_install_card_dismissed';

function readDismissed(): boolean {
  try { return localStorage.getItem(INSTALL_DISMISSED_KEY) === '1'; } catch { return false; }
}

export function InstallAppCard({ store = appInstallPrompt }: { store?: InstallPromptStore }) {
  const snap = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const [dismissed, setDismissed] = useState(readDismissed);
  const ios = isIosDevice(navigator);
  const state = installCardState({
    standalone: isStandalone(window),
    dismissed,
    installed: snap.installed,
    ios,
    canPrompt: !!snap.event,
  });
  if (state === 'hidden') return null;

  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(INSTALL_DISMISSED_KEY, '1'); } catch { /* private mode: hidden for this visit only */ }
  };

  if (state === 'prompt') {
    return (
      <PhoneCard icon={<PhoneIcon />} title="Install the app" onClose={dismiss}>
        <p>Storiz on your home screen, full screen, with alerts.</p>
        <div className="mt-2">
          <Button size="sm" onClick={() => { void store.prompt(); }}>Install</Button>
        </div>
      </PhoneCard>
    );
  }

  return (
    <PhoneCard icon={<PhoneIcon />} title="Install the app" onClose={dismiss}>
      <ol className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1">
        <li className="inline-flex items-center gap-1">
          <span>Tap Share</span>
          <span className="inline-grid place-items-center w-5 h-5 rounded bg-gray-100 text-[#0A84FF]"><ShareIcon /></span>
        </li>
        <li aria-hidden className="text-gray-400">→</li>
        <li className="inline-flex items-center gap-1">
          <span>Add to Home Screen</span>
          <span className="inline-grid place-items-center w-5 h-5 rounded bg-gray-100 text-gray-700"><AddSquareIcon /></span>
        </li>
        <li aria-hidden className="text-gray-400">→</li>
        <li className="inline-flex items-center gap-1">
          <span>open Storiz from the new icon</span>
          <img src="/icons/apple-touch-icon.png" alt="" className="w-5 h-5 rounded-[5px]" />
        </li>
      </ol>
    </PhoneCard>
  );
}
